// Release B-Fidelity-2a · Slice E frontend — Applicant Document Uploads.
// Route: /access-request/:token/documents (public, token-authenticated)
import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#12233F";
const CREAM = "#F5F1E8";
const GOLD = "#C6A15B";

const STATUS_COLOR = {
  submitted: "#0ea5e9", pending_review: "#f59e0b",
  verified: "#22c55e", rejected: "#dc2626", expired: "#64748b",
};

export default function AccessDocuments() {
  const { token } = useParams();
  const [docs, setDocs] = useState([]);
  const [limits, setLimits] = useState({ max_bytes: 10_485_760, allowed_mime: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState("other");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/access-requests/documents/${token}`);
      setDocs(r.data.documents || []);
      setLimits(r.data.limits || {});
      setLoading(false);
    } catch (e) {
      setError(e?.response?.data?.detail?.code || "load_failed");
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    setUploadError(null);
    if (!file) { setUploadError("Please choose a file."); return; }
    if (file.size > (limits.max_bytes || 10_485_760)) {
      setUploadError(`File too large. Max allowed is ${prettyBytes(limits.max_bytes || 10_485_760)}.`); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      if (label.trim()) fd.append("label", label.trim());
      await axios.post(`${API}/public/access-requests/documents/${token}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null); setLabel(""); setCategory("other");
      const input = document.getElementById("doc-file-input");
      if (input) input.value = "";
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setUploadError(d?.message || d?.code || "upload_failed");
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Withdraw this document?")) return;
    try {
      await axios.delete(`${API}/public/access-requests/documents/${token}/${id}`);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail?.message || "Could not withdraw. If Owner review has started, contact Edwin's team.");
    }
  };

  if (loading) return <Wrapper><Panel><p>Loading…</p></Panel></Wrapper>;
  if (error) return (
    <Wrapper><Panel>
      <h1 style={h1Style}>This link cannot be used</h1>
      <p data-testid="doc-error-code">Reason: <code>{error}</code></p>
      <Link to="/" style={linkStyle}>← ERF Enterprises home</Link>
    </Panel></Wrapper>
  );

  return (
    <Wrapper>
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "48px 20px" }}>
        <div style={{ borderTop: `4px solid ${GOLD}`, paddingTop: 24 }}>
          <div style={eyebrowStyle}>ERF ConstructionOS · Portal Access</div>
          <h1 data-testid="doc-title" style={h1Style}>Upload supporting documents</h1>
          <p style={{ marginTop: 8, color: "#475569", fontSize: 14 }}>
            Upload the documents Edwin's team asked for (ID, insurance certs, W-9, licenses, etc.).
            Files are stored securely and only visible inside your access request.
          </p>
          <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
            Max size: <b>{prettyBytes(limits.max_bytes || 10_485_760)}</b> per file ·
            Accepted: <b>{(limits.allowed_mime || []).map(shortMime).join(", ")}</b>
          </div>
        </div>

        <form onSubmit={upload} style={{ marginTop: 24, background: "white", padding: 20, borderRadius: 4, border: "1px solid #e2e8f0" }}>
          <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: 20, margin: 0, color: NAVY }}>Add a document</h2>
          <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
            <label style={labelStyle}>
              <span>Category</span>
              <select data-testid="doc-category" value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
                {(limits.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label style={labelStyle}>
              <span>Label (optional)</span>
              <input data-testid="doc-label" type="text" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              <span>File</span>
              <input id="doc-file-input" data-testid="doc-file" type="file"
                     onChange={(e) => setFile(e.target.files?.[0] || null)}
                     accept={(limits.allowed_mime || []).join(",")}
                     style={{ ...inputStyle, padding: 8 }} />
            </label>
            {uploadError && (
              <div role="alert" data-testid="doc-upload-error"
                   style={{ background: "#fee2e2", color: "#991b1b", padding: 10, borderRadius: 4, fontSize: 13 }}>
                {uploadError}
              </div>
            )}
            <div>
              <button type="submit" data-testid="doc-upload-btn" disabled={uploading || !file} style={primaryBtn}>
                {uploading ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        </form>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: 20, margin: 0, color: NAVY }}>Your uploads</h2>
          {docs.length === 0 && <p data-testid="doc-empty" style={{ fontSize: 13, color: "#475569", marginTop: 8 }}>No documents uploaded yet.</p>}
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            {docs.map((d) => (
              <div key={d.id} data-testid={`doc-row-${d.id}`}
                   style={{ background: "white", padding: 14, borderRadius: 4, border: "1px solid #e2e8f0",
                            display: "flex", gap: 12, justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{d.filename}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                    {d.category} · {shortMime(d.mime)} · {prettyBytes(d.size)} · uploaded {new Date(d.uploaded_at).toLocaleString()}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span data-testid={`doc-status-${d.id}`}
                        style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1,
                                 padding: "2px 8px", borderRadius: 9999,
                                 color: STATUS_COLOR[d.status] || "#64748b",
                                 background: (STATUS_COLOR[d.status] || "#64748b") + "22",
                                 border: `1px solid ${(STATUS_COLOR[d.status] || "#64748b")}55` }}>
                    {d.status}
                  </span>
                  {d.status === "submitted" && (
                    <button data-testid={`doc-remove-${d.id}`} type="button"
                            onClick={() => remove(d.id)}
                            style={{ fontSize: 12, background: "none", border: "none",
                                     color: "#dc2626", cursor: "pointer" }}>
                      Withdraw
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <p style={{ marginTop: 24, fontSize: 11, color: "#64748b" }}>
          Do not share this link. Every upload is logged and tied to your access request.
        </p>
      </div>
    </Wrapper>
  );
}

function prettyBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function shortMime(m) {
  if (!m) return "";
  const map = { "application/pdf": "PDF", "image/jpeg": "JPG", "image/png": "PNG",
    "image/webp": "WEBP", "text/plain": "TXT", "text/csv": "CSV",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX" };
  return map[m] || m;
}
function Wrapper({ children }) {
  return <main data-testid="documents-page" style={{ minHeight: "100vh", background: CREAM, color: NAVY }}>{children}</main>;
}
function Panel({ children }) {
  return <div style={{ maxWidth: 620, margin: "0 auto", padding: "80px 24px" }}>
    <div style={{ borderTop: `4px solid ${GOLD}`, paddingTop: 24 }}>{children}</div></div>;
}
const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 14, background: "white", color: "#0f172a", boxSizing: "border-box" };
const labelStyle = { display: "grid", gap: 6, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#475569", fontFamily: "'Space Mono', monospace" };
const h1Style = { fontFamily: "Playfair Display, serif", fontSize: 30, margin: "8px 0 4px", lineHeight: 1.1 };
const eyebrowStyle = { fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, color: GOLD, textTransform: "uppercase" };
const primaryBtn = { padding: "12px 22px", background: NAVY, color: CREAM, border: `2px solid ${GOLD}`, cursor: "pointer", fontSize: 13, letterSpacing: 1, textTransform: "uppercase", fontFamily: "'Space Mono', monospace" };
const linkStyle = { display: "inline-block", marginTop: 20, color: NAVY, textDecoration: "underline" };
